import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { ConfigHelpers } from '@eview/core/config/config.helpers';
import { SiteConfigItem } from '@eview/core/models/config';
import { User } from '@eview/core/models/user';
import { routePermissions } from '@eview/core/domain/navigation/navigation.helpers';
import { selectConfig } from '@eview/core/store/selectors/config.selector';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { selectUserPermissions } from '@eview/core/store/selectors/user-permissions.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of, race } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { selectTenant } from '@eview/core/store/selectors/tenant.selector';
import { AuthHelpers, AuthService } from '@eview/core/auth';
import { EAuthActions } from '../store/actions/auth.actions';
import { UserPermissions } from '../models/user-permissions';

@Injectable({
  providedIn: 'root'
})
export class PrivateSiteGuard {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly actions$ = inject(Actions);

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.resolveDashboardAccess(state.url).pipe(take(1));
  }

  canActivate(
    _next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean> {
    return combineLatest(this.store.select(selectTenant), this.private$).pipe(
      filter(([tenant]) => !!tenant),
      take(1),
      map(([tenant, isPrivate]) => {
        if (!isPrivate) {
          return true;
        }
        if (tenant.unique_name) {
          return true;
        }
        this.router.navigate(['/login2']);
        return false;
      })
    );
  }

  siteConfig$: Observable<SiteConfigItem> = this.store.select(selectConfig).pipe(
    map(config => ConfigHelpers.GetConfigItem<SiteConfigItem>(SiteConfigItem, config))
  );

  private$: Observable<boolean> = this.siteConfig$.pipe(
    map(siteConfig => (siteConfig ? siteConfig.private : true))
  );

  user$: Observable<User> = this.store.select(selectUser);

  private resolveDashboardAccess(url: string): Observable<boolean> {
    const permissionPath = this.normalizePermissionPath(url);
    const requiresPermissionCheck = routePermissions.some(
      r =>
        permissionPath === r.path ||
        permissionPath.startsWith(r.path + '/') ||
        permissionPath.startsWith(r.path + '?')
    );

    return combineLatest([
      this.store.select(selectTenant),
      this.store.select(selectUser),
      this.store.select(selectUserPermissions)
    ]).pipe(
      filter(([tenant]) => !!tenant),
      switchMap(([tenant, user, userPermissions]) => {
        const activeRoute = this.dashboardSegment(url);

        if (this.isMapRoute(activeRoute)) {
          return of(true);
        }

        if (tenant && !user) {
          const publicPages: string[] = tenant.public_pages?.['public-pages'] ?? [];
          if (publicPages.includes(activeRoute)) {
            return of(true);
          }
          if (this.hasValidSessionToken()) {
            return this.waitForRestoredUser().pipe(
              switchMap(restored =>
                restored
                  ? this.evaluateAccess(url, requiresPermissionCheck, userPermissions)
                  : of(false)
              )
            );
          }
          return of(false);
        }

        if (!tenant || !user) {
          return of(false);
        }

        return this.evaluateAccess(url, requiresPermissionCheck, userPermissions);
      })
    );
  }

  /** Strip query/hash and named-outlet noise so `/dashboard/posts/resumen` matches. */
  private normalizePermissionPath(url: string): string {
    const bare = (url || '').split('?')[0].split('#')[0];
    // Match first absolute path segment like /dashboard/...
    const m = bare.match(/\/dashboard\/[^\s)]*/);
    return m ? m[0].replace(/\/+$/, '') : bare.replace(/\/+$/, '');
  }

  private evaluateAccess(
    url: string,
    requiresPermissionCheck: boolean,
    userPermissions: UserPermissions | null
  ): Observable<boolean> {
    if (!requiresPermissionCheck) {
      return of(true);
    }

    const permissionPath = this.normalizePermissionPath(url);

    if (userPermissions === null) {
      return this.store.select(selectUserPermissions).pipe(
        filter(perms => perms !== null),
        take(1),
        switchMap(() =>
          AuthHelpers.User.HasRouteAccessPermission(this.store, permissionPath)
        ),
        take(1)
      );
    }

    return AuthHelpers.User.HasRouteAccessPermission(
      this.store,
      permissionPath
    ).pipe(take(1));
  }

  /** Wait for GetCurrentUser after refresh (user is not rehydrated from localStorage). */
  private waitForRestoredUser(): Observable<User | null> {
    return race(
      this.store.select(selectUser).pipe(
        filter((u): u is User => !!u),
        take(1)
      ),
      this.actions$.pipe(
        ofType(EAuthActions.GetCurrentUserFailure),
        take(1),
        map(() => null)
      )
    );
  }

  private hasValidSessionToken(): boolean {
    const { token } = this.authService.init();
    return !!(token && this.authService.hasRefreshToken(token));
  }

  private dashboardSegment(url: string): string {
    const marker = '/dashboard/';
    const idx = url.indexOf(marker);
    if (idx === -1) {
      return '';
    }
    return url.slice(idx + marker.length);
  }

  private isMapRoute(activeRoute: string): boolean {
    return activeRoute === 'map' || activeRoute.startsWith('map/');
  }
}
